import { IMaterialRequestBreakdown } from '../../../interfaces/Material.Interface';

/**
 * PTB-5351 per-bar receiving helpers.
 *
 * The `material_request_breakdown` is a cutting plan: a list of bars, each with a
 * `total_length_used` (the reservation minimum for that bar). When receiving, the
 * operator confirms an actual length per distinct bar requirement. A bar that meets
 * its requirement is reserved as planned. A shorter bar is still received: the
 * operator confirms which jobs to keep, and the rest are reordered (PTB-7036).
 */

export interface BarGroup {
	requiredLength: number;
	quantity: number;
	/**
	 * The plan's stock length for this group's bars (PTB-6452) — the default
	 * for the row's actual-length input, so a 61" remainder bar defaults to 61
	 * instead of the order's full stock length. When bars with different stock
	 * lengths share one requirement, the largest wins so no default understates
	 * a bar.
	 */
	barLength: number;
}

export interface ReceivedBar {
	required_length: number;
	actual_material_length: number;
	quantity: number;
	/** One kept-job list per physical bar, only for bars shorter than planned. */
	kept_by_bar?: string[][];
	/** Breakdown index of each planned short bar, same order as kept_by_bar. */
	plan_indices?: number[];
}

export interface ShortPlanJob {
	order_line_item_id: string;
	name: string;
	inches: number;
	keep: boolean;
	scheduled_start?: string | null;
}

export interface ShortPlanBar {
	required_length: number;
	actual_material_length: number;
	plan_index: number;
	jobs: ShortPlanJob[];
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/** Stable string key for a required length (used to index the per-group actual map). */
export const requiredKey = (n: number): string => String(round2(n));

const collectGroups = (
	breakdown: IMaterialRequestBreakdown[],
	vendorPartNumber: string,
	stockLength?: number
): BarGroup[] => {
	const counts = new Map<string, number>();
	const stockLengths = new Map<string, number>();
	for (const bar of breakdown || []) {
		if (bar.vendor_part_number !== vendorPartNumber || bar.reserved) continue;
		const stock = round2(bar.bar_length || 0);
		if (stockLength != null && stock !== stockLength) continue;
		const req = round2(bar.total_length_used || 0);
		if (req <= 0) continue;
		const key = requiredKey(req);
		counts.set(key, (counts.get(key) || 0) + 1);
		stockLengths.set(key, Math.max(stockLengths.get(key) || 0, stock));
	}
	return [...counts.entries()]
		.map(([key, quantity]) => ({
			requiredLength: Number(key),
			quantity,
			barLength: stockLengths.get(key) || 0,
		}))
		.sort((a, b) => b.requiredLength - a.requiredLength);
};

/**
 * Group the not-yet-reserved plan bars for a vendor part by their required length,
 * so the receiving UI can render one row per distinct requirement.
 *
 * PTB-6453: `generate_items` creates one PO line item per distinct bar length, so
 * a multi-length plan yields several receiving rows against one order. Pass the
 * line item's own `itemLength` to scope the rows to ITS plan bars — otherwise
 * every row's modal would offer (and consume) the sibling items' bars. When the
 * item length matches no plan bar (PTB-5351's manually-edited-length scenario),
 * fall back to the vendor-wide view rather than silently collapsing the modal
 * to simple mode.
 */
export function getUnreservedBarGroups(
	breakdown: IMaterialRequestBreakdown[] = [],
	vendorPartNumber: string,
	itemLength?: number | null
): BarGroup[] {
	if (itemLength != null && itemLength > 0) {
		const scoped = collectGroups(
			breakdown,
			vendorPartNumber,
			round2(itemLength)
		);
		if (scoped.length) return scoped;
	}
	return collectGroups(breakdown, vendorPartNumber);
}

/**
 * What is still outstanding against the ordered quantity, floored at zero
 * (PTB-6501).
 *
 * Over-received extras push `quantity_total_received` past `quantity_ordered`,
 * and a raw subtraction then goes negative — which leaked into the default
 * quantity, the amber hint and the confirmation dialog ("5 more than the
 * remaining ordered quantity (-3)"). Extras never make the order owe less than
 * nothing.
 */
export function remainingOrderedQuantity(
	quantityOrdered: number,
	quantityTotalReceived: number
): number {
	return Math.max(0, (quantityOrdered || 0) - (quantityTotalReceived || 0));
}

/**
 * Wording for the over-receipt confirmation (PTB-6501).
 *
 * In per-bar mode the row overage is what the operator just saw on screen, so
 * it leads. The order-level figure is a different counter — it can disagree
 * legitimately — so it is only added as an explicitly labelled total.
 */
export function buildOverReceiptMessage({
	qtyDelivered,
	remainingOrdered,
	perBarExcess,
}: {
	qtyDelivered: number;
	remainingOrdered: number;
	perBarExcess: number;
}): string {
	const beyondOrdered = qtyDelivered - remainingOrdered;
	if (perBarExcess > 0) {
		const head = `${perBarExcess} bar(s) are more than the cut plan asked for on their row. The extra bars will be added to inventory without reservations, and any bars still planned stay outstanding.`;
		return beyondOrdered > 0
			? `${head} This receive is in total ${beyondOrdered} more than the remaining ordered quantity (${remainingOrdered}).`
			: head;
	}
	return `Received Quantity is ${qtyDelivered}, which is ${beyondOrdered} more than the remaining ordered quantity (${remainingOrdered}). The extra bars will be added to inventory without reservations.`;
}

/**
 * Total bars entered beyond what each row planned (PTB-6499).
 *
 * The submit confirmation used to key off the order total alone, so entering
 * 0 / 0 / 4 against rows planned 1 / 2 / 1 slipped through silently: four bars
 * received, four planned, but three of them piled onto one requirement. Excess
 * has to be measured per row, not on the total.
 */
export function getPerBarExcess(
	groups: BarGroup[],
	quantityByRequired: Record<string, number>
): number {
	return groups.reduce((excess, g) => {
		const entered = quantityByRequired[requiredKey(g.requiredLength)];
		const qty = entered == null || Number.isNaN(entered) ? g.quantity : entered;
		return excess + Math.max(0, qty - g.quantity);
	}, 0);
}

/**
 * Validate the per-group actual lengths. A length below the plan is not an
 * error: receiving continues and the operator chooses which jobs to keep.
 * A missing length still blocks submit. Zero or negative lengths are rejected
 * here so the operator sees a clear message instead of a generic API 400.
 */
export function validateReceivedBars(
	groups: BarGroup[],
	actualByRequired: Record<string, number>
): string[] {
	const errors: string[] = [];
	for (const g of groups) {
		const actual = actualByRequired[requiredKey(g.requiredLength)];
		if (actual == null || Number.isNaN(actual)) {
			errors.push(
				`Enter an actual length for the ${g.requiredLength}" bar(s).`
			);
		} else if (!(actual > 0)) {
			errors.push(
				`Actual length for the ${g.requiredLength}" bar(s) must be greater than 0.`
			);
		}
	}
	return errors;
}

/** True when this row arrived shorter than the cuts planned on it. */
export function isShortBar(row: {
	required_length: number;
	actual_material_length: number;
}): boolean {
	return row.actual_material_length + 0.01 < row.required_length;
}

/**
 * Copy the operator's keep/drop choices onto the short rows, in the same order
 * the plan endpoint listed those bars.
 */
export function attachKeptJobs(
	rows: ReceivedBar[],
	planBars: ShortPlanBar[]
): ReceivedBar[] {
	const queues = new Map<string, ShortPlanBar[]>();
	for (const bar of planBars) {
		const key = requiredKey(bar.required_length);
		const list = queues.get(key) || [];
		list.push(bar);
		queues.set(key, list);
	}
	return rows.map((row) => {
		if (!isShortBar(row)) return row;
		const queue = queues.get(requiredKey(row.required_length)) || [];
		const slice = queue.splice(0, row.quantity);
		// Invalid plan_index would serialize poorly and let the backend fall
		// back to same-length matching. Drop those so hasUnplannedShortBars
		// blocks submit instead of binding keep/drop to the wrong row.
		const planned = slice.filter((bar) => Number.isInteger(bar.plan_index));
		return {
			...row,
			kept_by_bar: planned.map((bar) =>
				bar.jobs
					.filter((job) => job.keep)
					.map((job) => job.order_line_item_id)
			),
			plan_indices: planned.map((bar) => bar.plan_index),
		};
	});
}

/**
 * True when a short row is missing a keep list or plan index for a planned
 * bar. Extra bars beyond the plan (per-row over-receipt) do not need a keep
 * list — they are booked as unreserved inventory.
 */
export function hasUnplannedShortBars(
	rows: ReceivedBar[],
	planBars: ShortPlanBar[] = []
): boolean {
	const availableByLength = new Map<string, number>();
	for (const bar of planBars) {
		const key = requiredKey(bar.required_length);
		availableByLength.set(key, (availableByLength.get(key) || 0) + 1);
	}
	return rows.some((bar) => {
		if (!isShortBar(bar)) return false;
		const expected = Math.min(
			bar.quantity,
			availableByLength.get(requiredKey(bar.required_length)) || 0
		);
		return (
			(bar.kept_by_bar?.length ?? 0) !== expected ||
			(bar.plan_indices?.length ?? 0) !== expected
		);
	});
}

/** A planned short bar with no piece jobs cannot be confirmed. */
export function shortPlanHasEmptyBar(bars: ShortPlanBar[]): boolean {
	return bars.some((bar) => bar.jobs.length === 0);
}

/**
 * True when the kept jobs need more inches than the received bar. Matches
 * ``short_bar_errors`` / ``select_jobs_to_keep``: only selectable piece jobs
 * count. Trim-only / kerf footage is not part of that check on the server.
 */
export function shortPlanSelectionOverflow(bars: ShortPlanBar[]): boolean {
	return bars.some((bar) => {
		const used = bar.jobs
			.filter((job) => job.keep)
			.reduce((sum, job) => sum + job.inches, 0);
		return used > bar.actual_material_length + 0.01;
	});
}

/**
 * Coerce planner decimals that may arrive as strings so modal arithmetic and
 * `toFixed()` always see numbers.
 */
export function normalizeShortPlanBars(bars: unknown[]): ShortPlanBar[] {
	return (bars || []).map((raw: any) => ({
		...raw,
		required_length: Number(raw.required_length),
		actual_material_length: Number(raw.actual_material_length),
		plan_index: Number(raw.plan_index),
		jobs: (raw.jobs || []).map((job: any) => ({
			...job,
			inches: Number(job.inches),
			keep: Boolean(job.keep),
		})),
	}));
}

/** Recursively collect string messages from nested DRF validation payloads. */
function collectErrorMessages(value: unknown): string[] {
	if (typeof value === 'string') return value ? [value] : [];
	if (Array.isArray(value)) return value.flatMap(collectErrorMessages);
	if (value && typeof value === 'object') {
		return Object.values(value).flatMap(collectErrorMessages);
	}
	return [];
}

/** Text for a failed receive. RTK Query puts the body on `data`, not `error`. */
export function receiveErrorMessage(error: any): string {
	const fallback = 'There was an error receiving the material.';
	const data = error?.data;
	if (typeof data === 'string' && data) return data;
	if (data && typeof data === 'object') {
		if (typeof data.detail === 'string' && data.detail) return data.detail;
		if (typeof data.error === 'string' && data.error) return data.error;
		const parts = collectErrorMessages(data);
		if (parts.length) return parts.join(' ');
	}
	if (typeof error?.error === 'string' && error.error) return error.error;
	return fallback;
}

/**
 * Build the `received_bars` payload the backend expects. When `quantityByRequired`
 * is given, it overrides each group's quantity — below the group's available
 * quantity for partial deliveries, or above it for over-receipt (the extra bars
 * are received as unreserved inventory); otherwise the full group quantity is used.
 */
export function buildReceivedBarsPayload(
	groups: BarGroup[],
	actualByRequired: Record<string, number>,
	quantityByRequired?: Record<string, number>
): ReceivedBar[] {
	return groups
		.map((g) => {
			const key = requiredKey(g.requiredLength);
			// Present (even 0) overrides; absent defaults to the full group quantity.
			const hasEntered =
				quantityByRequired != null && key in quantityByRequired;
			const entered = hasEntered ? quantityByRequired![key] : g.quantity;
			const quantity = Math.max(0, entered ?? 0);
			return {
				required_length: g.requiredLength,
				actual_material_length: actualByRequired[key],
				quantity,
			};
		})
		.filter((row) => row.quantity > 0);
}
